import React, { useMemo } from "react";
import { isEmpty } from "lodash";

import { MainContainer, Skeleton } from "components";
import useLoader from "hooks/useLoader";
import {
	getResultComparisonTableData,
	LABELS_COMPARISON_OWNER_RESULT,
} from "mappers";
import { labelType } from "types";

import ComparisonReportTable from "./comparisonReportTable";

import styles from "./matchReport.module.scss";

type ComparisonPageProps = {
	loading: boolean;
	titleColumn: string;
	matchDetails?: { [key: string]: any };
	comparisonLabels: labelType[];
	PossibleButton: React.FC<{}>;
};

const ComparisonPage = ({
	loading,
	titleColumn,
	matchDetails,
	PossibleButton,
	comparisonLabels,
}: ComparisonPageProps) => {
	const Loader = useLoader(loading);

	const merchantMatchResultData = useMemo(() => {
		return matchDetails?.matches?.map(match => {
			return getResultComparisonTableData(
				matchDetails?.original_inquiry,
				match,
				comparisonLabels,
				match?.acquirer,
			);
		});
	}, [matchDetails, comparisonLabels]);

	const merchantOwnersResultData = useMemo(() => {
		return matchDetails?.matches?.map(match => {
			return getResultComparisonTableData(
				matchDetails?.original_inquiry?.owners.map(
					originalOwner => originalOwner,
				),
				match?.owners.map(matchOwner => matchOwner),
				LABELS_COMPARISON_OWNER_RESULT,
			);
		});
	}, [matchDetails?.matches, matchDetails?.original_inquiry?.owners]);

	// For the same length of two tables
	if (
		!isEmpty(merchantMatchResultData) &&
		!isEmpty(merchantOwnersResultData)
	) {
		const maxLength = Math.max(
			...merchantMatchResultData?.map(subArray => subArray.length),
			...merchantOwnersResultData?.map(subArray => subArray.length),
		);

		merchantMatchResultData.forEach(subArray => {
			while (subArray.length < maxLength) {
				subArray.push({});
			}
		});

		merchantOwnersResultData.forEach(subArray => {
			while (subArray.length < maxLength) {
				subArray.push({
					acquirerData: {},
					fieldName: "",
					inquiry: {},
					isMatch: false,
					merchantMatchResults: {},
					name: "",
				});
			}
		});
	}

	return (
		<>
			<PossibleButton />
			<MainContainer
				className={`${styles.comparisonContainer} px-0 py-0 mt-1`}
			>
				<Loader
					skeleton={
						<Skeleton height="calc(100vh - 190px)" variant="rect" />
					}
					component={
						<div className="d-flex">
							<div className="w-100">
								{!isEmpty(merchantMatchResultData) &&
									merchantMatchResultData?.map(
										(result, key) => (
											<div key={key} className="mr-3">
												<ComparisonReportTable
													data={result}
													isLoading={loading}
													index={key}
													titleColumn={titleColumn}
												/>
											</div>
										),
									)}
							</div>
							<div
								className={`${styles.comparisonTableContainer} w-100`}
							>
								{!isEmpty(merchantOwnersResultData) &&
									merchantOwnersResultData?.map(
										(result, key) => (
											<div key={key}>
												<ComparisonReportTable
													data={result}
													isLoading={loading}
													index={key}
													isOwnersReport
												/>
											</div>
										),
									)}
							</div>
						</div>
					}
				/>
			</MainContainer>
		</>
	);
};

export default ComparisonPage;
